import { CsvBulkConfig, CsvFieldDefinition } from 'components/csv/types';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import { CreatePartnerParams, createPartner } from 'stores/PartnerStoreAdapter';

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/**
 * パートナーCSV一括登録用のフィールド定義を生成する
 */
export const createPartnerCsvFields = (
  partnerTypes: MasterTypeResult[],
): CsvFieldDefinition[] => [
  {
    header: 'パートナー名',
    key: 'partnerName',
    description: 'パートナー名（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: 'パートナー名（カナ）',
    key: 'partnerNameKana',
    description: 'パートナー名カナ（必須、最大300文字）',
    required: true,
    type: 'text',
    maxLength: 300,
  },
  {
    header: 'パートナー区分ID',
    key: 'partnerTypeId',
    description: 'パートナー区分のコード値（必須）',
    required: true,
    type: 'radio',
    options: partnerTypes.map((t) => ({ code: t.typeCode, label: t.typeLabel })),
  },
  {
    header: '法人番号',
    key: 'corporateNumber',
    description: '法人番号（任意、最大20文字）',
    required: false,
    type: 'text',
    maxLength: 20,
  },
  {
    header: 'インボイス番号',
    key: 'invoiceNumber',
    description: '登録番号/インボイス番号 T+13桁（任意、最大20文字）',
    required: false,
    type: 'text',
    maxLength: 20,
  },
  {
    header: '住所',
    key: 'address',
    description: '住所（任意、最大500文字）',
    required: false,
    type: 'text',
    maxLength: 500,
  },
  {
    header: '電話番号',
    key: 'phoneNumber',
    description: '電話番号（任意、最大20文字）',
    required: false,
    type: 'text',
    maxLength: 20,
  },
  {
    header: 'メールアドレス',
    key: 'mailAddress',
    description: 'メールアドレス（任意、最大300文字）',
    required: false,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '担当者名',
    key: 'contactName',
    description: '担当者名（任意、最大300文字）',
    required: false,
    type: 'text',
    maxLength: 300,
  },
  {
    header: '契約開始日',
    key: 'contractStartDate',
    description: "契約開始日（必須、format: yyyy-MM-dd'T'HH:mm:ss）",
    required: true,
    type: 'text',
  },
  {
    header: '備考',
    key: 'remarks',
    description: '備考（任意、最大10000文字）',
    required: false,
    type: 'text',
    maxLength: 10000,
  },
];

/**
 * パートナーCSV一括登録のConfig生成関数
 */
export const createPartnerBulkConfig = (
  partnerTypes: MasterTypeResult[],
): CsvBulkConfig<CreatePartnerParams> => {
  const fields = createPartnerCsvFields(partnerTypes);

  return {
    entityName: 'パートナー',
    fields,
    toApiParams: (row: Record<string, string>): CreatePartnerParams => {
      const params: CreatePartnerParams = {
        partnerName: row.partnerName,
        partnerNameKana: row.partnerNameKana,
        partnerTypeId: row.partnerTypeId,
        contractStartDate: row.contractStartDate,
      };

      if (row.corporateNumber) params.corporateNumber = row.corporateNumber;
      if (row.invoiceNumber) params.invoiceNumber = row.invoiceNumber;
      if (row.address) params.address = row.address;
      if (row.phoneNumber) params.phoneNumber = row.phoneNumber;
      if (row.mailAddress) params.mailAddress = row.mailAddress;
      if (row.contactName) params.contactName = row.contactName;
      if (row.remarks) params.remarks = row.remarks;

      return params;
    },
    registerOne: createPartner,
    resolveDisplayValue: (key: string, rawValue: string): string | null => {
      if (!rawValue) return null;

      const field = fields.find((f) => f.key === key);
      if (!field || !field.options) return null;

      const option = field.options.find((o) => o.code === rawValue);
      return option ? option.label : null;
    },
    validateField: (key: string, value: string, _row: Record<string, string>): string | null => {
      // 契約開始日のフォーマットチェック
      if (key === 'contractStartDate' && value && !DATETIME_REGEX.test(value)) {
        return "契約開始日はyyyy-MM-dd'T'HH:mm:ss形式で入力してください";
      }

      return null;
    },
  };
};
